import { shine } from '@shinetools/invoicing-calculation'

import { InvoiceDiscountType } from '../enums/invoiceDiscountType'
import type { InvoiceFormRowLine } from '../types/invoiceForm/invoiceFormLine/invoiceFormRowLine'
import { clampInvoiceDiscountValue } from './clampInvoiceDiscountValue'
import { isPercentageDiscountType } from './isPercentageDiscountType'

export type DiscountValidationIssue = shine.model.ValidationIssue

export interface DiscountableLineInput {
  discount?: number
  quantity: string
  unitPrice: string
  vatRate: string
}

interface LineCalculationResult {
  discountAmount: number
  lineTotal: number
  totalExcludingVat: number
  totalIncludingVat: number
  validationIssues: DiscountValidationIssue[]
  vatAmount: number
}

export interface InvoiceCalculationResult {
  lines: LineCalculationResult[]
  totalDiscountAmount: number
  totalGrossAmount: number
  totalLineAmount: number
  totalNetAmount: number
  totalVatAmount: number
  validationIssues: DiscountValidationIssue[]
}

export class InvoiceDiscountCalculator {
  private static buildDiscountInput(
    discount: number,
    discountType: InvoiceDiscountType,
  ): shine.model.DiscountInput | undefined {
    if (discount <= 0) {
      return undefined
    }

    const clampedDiscount = clampInvoiceDiscountValue(discount, discountType)

    return isPercentageDiscountType(discountType)
      ? shine.Discount.rate(String(clampedDiscount / 100))
      : shine.Discount.amount(String(clampedDiscount))
  }

  private static mapLineResult(result: shine.model.LineResult): LineCalculationResult {
    return {
      discountAmount: Number(result.discountAmount),
      lineTotal: Number(result.lineTotal),
      totalExcludingVat: Number(result.netTotal),
      totalIncludingVat: Number(result.grossTotal),
      validationIssues: result.validationIssues,
      vatAmount: Number(result.vatAmount),
    }
  }

  static calculateLine(
    line: Pick<InvoiceFormRowLine, 'discount' | 'quantity' | 'unitPrice' | 'vatRatePercent'>,
    discountType: InvoiceDiscountType,
    currency: string,
  ): LineCalculationResult {
    return InvoiceDiscountCalculator.mapLineResult(
      shine.InvoiceLine.calculate(
        String(line.unitPrice),
        String(line.quantity),
        String(line.vatRatePercent ?? 0),
        InvoiceDiscountCalculator.buildDiscountInput(line.discount, discountType),
        currency,
      ),
    )
  }

  // Lines can carry their own discount (ByItem mode) and/or an invoice-level discount can be
  // passed on top (Global mode) - the package distributes/reconciles totals across both in one
  // call, so callers never need to loop over lines and sum discounted totals themselves.
  static calculateInvoice(
    lines: DiscountableLineInput[],
    discountType: InvoiceDiscountType,
    currency: string,
    globalDiscount?: number,
  ): InvoiceCalculationResult {
    const lineInputs = lines.map(
      (line) =>
        new shine.model.InvoiceLineInput(
          line.unitPrice,
          line.quantity,
          line.vatRate,
          InvoiceDiscountCalculator.buildDiscountInput(line.discount ?? 0, discountType),
        ),
    )

    const result = shine.Invoice.calculate(
      lineInputs,
      InvoiceDiscountCalculator.buildDiscountInput(globalDiscount ?? 0, discountType),
      currency,
    )

    return {
      lines: result.lines.map(InvoiceDiscountCalculator.mapLineResult),
      totalDiscountAmount: Number(result.totalDiscountAmount),
      totalGrossAmount: Number(result.totalGrossAmount),
      totalLineAmount: Number(result.totalLineAmount),
      totalNetAmount: Number(result.totalNetAmount),
      totalVatAmount: Number(result.totalVatAmount),
      validationIssues: result.validationIssues,
    }
  }

  static hasDiscountExceedsBaseIssue(validationIssues: DiscountValidationIssue[], lineIndex?: number): boolean {
    return validationIssues.some(
      (issue) =>
        issue.code === shine.model.ValidationCode.DISCOUNT_EXCEEDS_BASE &&
        (lineIndex === undefined || issue.lineIndex === lineIndex),
    )
  }
}
